/**
 * 현장 특이사항 위젯 — 댓글처럼 한 줄 적고 Enter 만 치면 등록된다.
 *
 * 별도 엔티티를 만들지 않았다. 특이사항은 "언제인지 모르지만 남겨둬야 하는
 * 현장 메모" 그 자체이고, 그건 이미 site_schedules 의 event_type='memo'
 * (scheduled_at=null) 가 담당한다. 여기서 남긴 글은 메모 페이지·현장 일정의
 * 메모 목록에서도 그대로 보인다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Campaign as NoteIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import WidgetCard from '../WidgetCard';
import AuthorChip from '../../../components/AuthorChip';
import { canEditSchedule } from '../../../components/dialogs/ScheduleFormDialog';
import { SiteScheduleService } from '../../../services/SiteScheduleService';
import { MEMO_EVENT_TYPE, SiteSchedule } from '../../../models/SiteSchedule';

interface NotesWidgetProps {
  projectId: number;
  /** "메모 전체" 액션 — 부모가 사이드바 섹션을 전환한다. */
  onActionClick?: () => void;
}

const TITLE_MAX = 255; // entity.SiteSchedule.Title varchar(255)

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
};

const NotesWidget: React.FC<NotesWidgetProps> = ({ projectId, onActionClick }) => {
  const writable = canEditSchedule();
  const [notes, setNotes] = useState<SiteSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      // 기간 인자를 주면 scheduled_at IS NULL 인 메모가 통째로 빠진다 (MemoPage 주석 참고).
      const list = await SiteScheduleService.list(projectId);
      setNotes(
        list
          .filter((s) => s.event_type === MEMO_EVENT_TYPE)
          .sort((a, b) => b.created_at.localeCompare(a.created_at)),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    try {
      await SiteScheduleService.create(projectId, {
        event_type: MEMO_EVENT_TYPE,
        status: 'planned',
        title: text.slice(0, TITLE_MAX),
        // 255자를 넘긴 긴 글은 잘린 제목 + 원문 본문으로 나눠 담는다.
        description: text.length > TITLE_MAX ? text : undefined,
        scheduled_at: null,
      });
      setDraft('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '등록 실패');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n: SiteSchedule) => {
    if (!window.confirm('이 특이사항을 삭제하시겠습니까?')) return;
    try {
      await SiteScheduleService.delete(n.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제 실패');
    }
  };

  return (
    <WidgetCard
      title={`현장 특이사항 ${notes.length > 0 ? `(${notes.length}건)` : ''}`}
      icon={<NoteIcon fontSize="small" />}
      actionLabel={onActionClick ? '메모 전체' : undefined}
      onAction={onActionClick}
      testId="widget-notes"
    >
      <Stack sx={{ height: '100%', minHeight: 0 }} spacing={1}>
        {writable && (
          <TextField
            variant="standard"
            size="small"
            fullWidth
            multiline
            maxRows={4}
            placeholder="특이사항을 적고 Enter (줄바꿈은 Shift+Enter)"
            value={draft}
            disabled={saving}
            // 카드가 이미 테두리다 — 입력창까지 테두리를 두면 액자가 두 겹이 된다.
            InputProps={{ disableUnderline: true }}
            sx={{ '& .MuiInputBase-root': { py: 0.25 } }}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              // IME 조합 중인 Enter 는 한글 확정용이라 등록으로 받으면 안 된다.
              if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
              e.preventDefault();
              void submit();
            }}
            inputProps={{ 'data-testid': 'note-input' }}
          />
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            아직 특이사항이 없습니다.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {notes.map((n) => (
              <Box key={n.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {n.title}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                    <AuthorChip name={n.created_by_name} userId={n.created_by_id} label="" dense />
                    <Typography variant="caption" color="text.secondary">
                      {formatWhen(n.created_at)}
                    </Typography>
                  </Stack>
                </Box>
                {writable && (
                  <Tooltip title="삭제">
                    <IconButton size="small" onClick={() => remove(n)}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </WidgetCard>
  );
};

export default NotesWidget;
